import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Button from '../components/common/Button';
import Logo from '../components/common/Logo';
import { MAX_LOAN_LIMIT } from '../utils/constants';
import useAuthStore from '../stores/authStore';

const Welcome = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isProfileComplete } = useAuthStore();

  useEffect(() => {
    // Redirect authenticated users to appropriate page
    if (isAuthenticated) {
      if (isProfileComplete()) {
        navigate('/dashboard');
      } else {
        navigate('/profile-step-1');
      }
    }
  }, [isAuthenticated, isProfileComplete, navigate]);

  const handleApplyNow = () => {
    navigate('/auth');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-blue-50 to-primary-100">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          {/* Logo and Brand */}
          <div className="mb-8">
            <Logo size="2xl" className="mx-auto mb-6 shadow-lg" />
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
              CreditPesa Loans
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8">
              Quick loans directly to your M-Pesa account
            </p>
          </div>

          {/* Loan Limit Showcase */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 mb-12 border border-gray-100">
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-8 text-white mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Your Loan Limit
              </h2>
              <p className="text-5xl md:text-6xl font-bold mb-4">
                KES {MAX_LOAN_LIMIT.toLocaleString()}
              </p>
              <p className="text-primary-100 text-lg">
                Maximum amount you can borrow
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Quick Approval</h3>
                <p className="text-gray-600">Get approved in just 48 hours</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Direct to M-Pesa</h3>
                <p className="text-gray-600">Funds sent directly to your mobile money</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Secure & Safe</h3>
                <p className="text-gray-600">Your data is protected and encrypted</p>
              </div>
            </div>

            {/* CTA Button */}
            <Button 
              onClick={handleApplyNow}
              size="lg"
              className="w-full md:w-auto px-12 py-4 text-lg font-semibold"
            >
              Apply Now
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Button>
          </div>

          {/* Process Steps */}
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">How It Works</h2>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-lg">
                  1
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Sign Up</h3>
                <p className="text-sm text-gray-600">Create account with email and PIN</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-lg">
                  2
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Complete Profile</h3>
                <p className="text-sm text-gray-600">Fill in your personal information</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-lg">
                  3
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Pay Service Fee</h3>
                <p className="text-sm text-gray-600">Pay small fee via M-Pesa</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-success-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-lg">
                  ✓
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Get Approved</h3>
                <p className="text-sm text-gray-600">Receive loan in 48 hours</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Indicators */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Trusted by Thousands</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="flex items-center justify-center">
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                  </svg>
                  <span className="text-lg font-semibold text-gray-900">4.8/5 Rating</span>
                </div>
              </div>
              
              <div className="flex items-center justify-center">
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM9 9a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span className="text-lg font-semibold text-gray-900">10,000+ Users</span>
                </div>
              </div>
              
              <div className="flex items-center justify-center">
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span className="text-lg font-semibold text-gray-900">100% Secure</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Welcome;